/**
 * FormulaReference Component
 *
 * Displays a collapsible reference panel for mathematical formulas.
 * Unlike FormulaPalette (which has clickable buttons), this component
 * simply displays the formulas as a reference that participants can view.
 *
 * Props:
 * - formulas: String containing formulas with LaTeX notation (e.g., "$P = F/A$; $1 atm = 760 mmHg$")
 * - onInsert: optional (formula: string) => void. When provided, each formula
 *   line becomes clickable and inserts that formula into the message box.
 */
import React, { useState } from "react";
import "katex/dist/katex.min.css";
import { InlineMath, BlockMath } from "react-katex";

export function FormulaReference({ formulas, onInsert }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!formulas || formulas === "NA" || formulas === "N/A") {
    return null;
  }

  // Parse the formula string into individual formulas. Split on ";" ONLY when
  // it's a real separator — not inside a $...$ math span and not inside
  // parentheses (e.g. the ideal-gas-law note "($R = ...$; temperatures in
  // kelvin)" must stay one line, not split into a stray fragment).
  const parseFormulas = (formulaString) => {
    const parts = [];
    let buf = "", depth = 0, inMath = false;
    for (const ch of formulaString) {
      if (ch === "$") inMath = !inMath;
      else if (!inMath && ch === "(") depth++;
      else if (!inMath && ch === ")") depth = Math.max(0, depth - 1);
      if (ch === ";" && !inMath && depth === 0) { parts.push(buf); buf = ""; continue; }
      buf += ch;
    }
    parts.push(buf);
    return parts.map(p => p.trim()).filter(Boolean);
  };

  const formulaParts = parseFormulas(formulas);

  return (
    <div className="formula-reference">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 transition-colors py-1 px-2 rounded hover:bg-indigo-50"
      >
        <span className="text-lg">∑</span>
        <span>{isExpanded ? "Hide formulas" : "View formulas"}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Formula display panel */}
      {isExpanded && (
        <div className="mt-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
          <p className="text-xs text-indigo-600 mb-2 font-medium">
            Reference Formulas:{onInsert ? " (click a formula to add it to your message)" : ""}
          </p>
          <div className="space-y-2">
            {formulaParts.map((part, index) => (
              <FormulaLine key={index} formula={part} onInsert={onInsert} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * FormulaLine Component
 * Renders a single formula line, handling both inline $...$ and block $$...$$ LaTeX
 */
function FormulaLine({ formula, onInsert }) {
  const hasBlockMath = formula.includes("$$");
  const hasInlineMath = formula.includes("$") && !hasBlockMath;

  let content;
  if (hasBlockMath) {
    const parts = formula.split(/\$\$/);
    content = parts.map((part, i) => {
      if (i % 2 === 1) {
        try { return <BlockMath key={i} math={part} />; }
        catch (e) { return <code key={i} className="text-red-600">{part}</code>; }
      }
      return part ? <span key={i}>{part}</span> : null;
    });
  } else if (hasInlineMath) {
    const parts = formula.split(/(\$[^$]+\$)/);
    content = parts.map((part, i) => {
      if (part.startsWith("$") && part.endsWith("$")) {
        const math = part.slice(1, -1);
        try { return <InlineMath key={i} math={math} />; }
        catch (e) { return <code key={i} className="text-red-600">{math}</code>; }
      }
      return part ? <span key={i}>{part}</span> : null;
    });
  } else {
    content = formula;
  }

  const clickable = typeof onInsert === "function";
  return (
    <div
      className={`text-sm text-gray-700 bg-white rounded p-2 border border-indigo-100 ${
        clickable ? "cursor-pointer hover:bg-indigo-50 hover:border-indigo-300 transition-colors flex items-center justify-between gap-2" : ""
      }`}
      onClick={clickable ? () => onInsert(formula) : undefined}
      role={clickable ? "button" : undefined}
      title={clickable ? "Click to insert this formula into your message" : undefined}
    >
      <span>{content}</span>
      {clickable && <span className="text-indigo-400 text-xs shrink-0 whitespace-nowrap">+ insert</span>}
    </div>
  );
}
